import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Wand2, UserRound, AlertTriangle, Repeat, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { dayjs, LEAD } from "@/lib/constants";
import { useHotel } from "@/context/HotelContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard } from "@/components/Common";
import { HotelSegment, useStrictHotel } from "@/components/HotelSegment";

const monday = (d) => d.subtract((d.day() + 6) % 7, "day").startOf("day");

const StaffSelect = ({ value, onChange, options, testid }) => (
  <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? null : v)}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="none">— Nessuno —</SelectItem>
      {options.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.role}</SelectItem>)}
    </SelectContent>
  </Select>
);

const ShiftDialog = ({ shift, onClose, onSaved, staff }) => {
  const [v, setV] = useState(shift);
  const save = async () => {
    const status = v.substitute_id ? "sostituito" : v.staff_id ? "pianificato" : "scoperto";
    try {
      await api.put(`/shifts/${v.id}`, { staff_id: v.staff_id, substitute_id: v.substitute_id, note: v.note || "", status });
      toast.success("Turno aggiornato");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const subs = [...staff.filter((s) => ["Jolly", "Back-up"].includes(s.role)), ...staff.filter((s) => !["Jolly", "Back-up"].includes(s.role))];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200" data-testid="shift-jolly-assign-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">Turno {v.slot}</DialogTitle>
          <DialogDescription>{dayjs(v.date).format("dddd D MMMM YYYY")} · posizione {v.position + 1}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label className="text-xs text-slate-500">Operatore titolare</Label>
            <StaffSelect value={v.staff_id} onChange={(x) => setV({ ...v, staff_id: x })} options={staff} testid="shift-staff-select" /></div>
          <div className="space-y-1.5"><Label className="text-xs text-slate-500">Sostituto (Jolly / Back-up)</Label>
            <StaffSelect value={v.substitute_id} onChange={(x) => setV({ ...v, substitute_id: x })} options={subs} testid="shift-substitute-select" /></div>
          <div className="space-y-1.5"><Label className="text-xs text-slate-500">Nota</Label>
            <Input value={v.note || ""} onChange={(e) => setV({ ...v, note: e.target.value })} data-testid="shift-note-input" placeholder="Es. ferie, malattia, permesso" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={save} data-testid="shift-save-btn">Salva turno</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Cell = ({ s, staffName, onClick, canEdit }) => {
  if (!s) return <div className="h-14 rounded-md border border-dashed border-slate-200" />;
  const tone = s.status === "scoperto" ? "border-red-500/60 bg-red-50" : s.status === "sostituito" ? "border-sky-500/50 bg-sky-50" : "border-slate-200 bg-white";
  return (
    <button disabled={!canEdit} onClick={onClick} data-testid={`shift-cell-${s.date}-${s.slot.replace(/\W/g, "")}-${s.position}`}
      className={`h-14 w-full rounded-md border px-2 text-left transition-colors hover:border-amber-500/60 ${tone}`}>
      {s.status === "scoperto" ? (
        <span className="flex items-center gap-1 text-xs font-mono text-red-600"><AlertTriangle className="w-3 h-3" />SCOPERTO</span>
      ) : (
        <>
          <div className="text-xs text-slate-900 truncate flex items-center gap-1">
            {s.substitute_id ? <Repeat className="w-3 h-3 text-sky-600 shrink-0" /> : <UserRound className="w-3 h-3 text-slate-500 shrink-0" />}
            {staffName(s.substitute_id || s.staff_id)}
          </div>
          {s.substitute_id && <div className="text-[10px] text-slate-500 truncate line-through">{staffName(s.staff_id)}</div>}
        </>
      )}
    </button>
  );
};

export default function Turni() {
  const { hotels, staffFor, staffName } = useHotel();
  const { user } = useAuth();
  const [local, setLocal] = useState(null);
  const hid = useStrictHotel(local);
  const [week, setWeek] = useState(monday(dayjs()));
  const [shifts, setShifts] = useState([]);
  const [editing, setEditing] = useState(null);
  const hotel = hotels.find((h) => h.id === hid);
  const days = useMemo(() => [...Array(7)].map((_, i) => week.add(i, "day")), [week]);
  const canEdit = LEAD.includes(user.role);

  const load = useCallback(async () => {
    if (!hid) return;
    const { data } = await api.get("/shifts", { params: { hotel_id: hid, from: week.format("YYYY-MM-DD"), to: week.add(6, "day").format("YYYY-MM-DD") } });
    setShifts(data);
  }, [hid, week]);
  useEffect(() => { load(); }, [load]);

  const [exporting, setExporting] = useState(false);
  const exportPdf = async () => {
    setExporting(true);
    try { await downloadFile("/shifts/export", { hotel_id: hid, week_start: week.format("YYYY-MM-DD") }, `turni-${hotel?.code}-${week.format("YYYY-MM-DD")}.pdf`); toast.success("PDF turni scaricato"); }
    catch (e) { toast.error("Esportazione non riuscita"); }
    setExporting(false);
  };
  const generate = async () => {
    try { const { data } = await api.post("/shifts/generate", { hotel_id: hid, week_start: week.format("YYYY-MM-DD") }); toast.success(`${data.created} turni creati`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const map = useMemo(() => Object.fromEntries(shifts.map((s) => [`${s.date}|${s.slot}|${s.position}`, s])), [shifts]);
  const rows = hotel ? hotel.slots.flatMap((sl) => [...Array(sl.positions)].map((_, p) => ({ slot: sl.label, p }))) : [];
  const covered = shifts.filter((s) => s.status !== "scoperto").length;
  const hours = {};
  shifts.forEach((s) => { const id = s.substitute_id || s.staff_id; if (id) hours[id] = (hours[id] || 0) + 8; });

  return (
    <>
      <PageHeader eyebrow="Pianificazione presidio" title="Turni e Rota" subtitle={hotel?.notes}>
        <Button variant="outline" size="icon" onClick={() => setWeek(week.subtract(7, "day"))} data-testid="week-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
        <div className="font-mono text-sm px-2" data-testid="week-label">{week.format("DD MMM")} – {week.add(6, "day").format("DD MMM YYYY")}</div>
        <Button variant="outline" size="icon" onClick={() => setWeek(week.add(7, "day"))} data-testid="week-next-btn"><ChevronRight className="w-4 h-4" /></Button>
        <Button variant="ghost" onClick={() => setWeek(monday(dayjs()))} data-testid="week-today-btn">Oggi</Button>
        <Button variant="outline" className="gap-2" disabled={!shifts.length || exporting} onClick={exportPdf} data-testid="export-rota-pdf-btn">
          <FileDown className="w-4 h-4" />{exporting ? "…" : "Stampa PDF"}
        </Button>
      </PageHeader>
      <HotelSegment value={hid} onChange={setLocal} />
      {canEdit && shifts.length > 0 && (
        <div className="panel px-4 py-3 text-sm text-slate-600" data-testid="rota-help">
          Clicca su una cella per assegnare l'operatore titolare, inserire un sostituto (Jolly/Back-up) o lasciare il turno scoperto. Per pianificare una nuova settimana vai avanti con la freccia e usa "Genera da settimana precedente".
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="week-coverage" label="Copertura settimana" value={shifts.length ? Math.round((covered / shifts.length) * 1000) / 10 : 0} suffix="%" tone={covered === shifts.length && shifts.length ? "ok" : "bad"} target="100%" />
        <StatCard testid="week-shifts" label="Turni pianificati" value={shifts.length} />
        <StatCard testid="week-subs" label="Sostituzioni" value={shifts.filter((s) => s.substitute_id).length} tone="neutral" hint="Jolly / Back-up" />
        <StatCard testid="week-uncovered" label="Scoperti" value={shifts.length - covered} tone={shifts.length - covered ? "bad" : "ok"} />
      </div>
      {!shifts.length ? (
        <div className="panel p-10 text-center">
          <div className="text-slate-500 mb-4">Nessun turno pianificato per questa settimana.</div>
          {canEdit && <Button onClick={generate} className="gap-2" data-testid="generate-week-btn"><Wand2 className="w-4 h-4" />Genera da settimana precedente</Button>}
        </div>
      ) : (
        <div className="panel p-4 overflow-x-auto" data-testid="rota-grid">
          <div className="grid gap-2 min-w-[900px]" style={{ gridTemplateColumns: "150px repeat(7, 1fr)" }}>
            <div />
            {days.map((d) => (
              <div key={d.format()} className={`text-center pb-2 ${d.isSame(dayjs(), "day") ? "text-amber-600" : "text-slate-500"}`}>
                <div className="eyebrow">{d.format("ddd")}</div><div className="font-heading font-semibold">{d.format("DD/MM")}</div>
              </div>
            ))}
            {rows.map(({ slot, p }) => (
              <div key={`${slot}${p}`} className="contents">
                <div className="flex flex-col justify-center pr-2">
                  <div className="font-mono text-xs text-slate-800">{slot}</div>
                  <div className="text-[10px] text-slate-500">{hotel.slots.find((x) => x.label === slot).positions > 1 ? (p === 0 ? "Shift Leader / Control Room" : "Door Supervisor") : "Operatore"}</div>
                </div>
                {days.map((d) => {
                  const s = map[`${d.format("YYYY-MM-DD")}|${slot}|${p}`];
                  return <Cell key={d.format()} s={s} staffName={staffName} canEdit={canEdit} onClick={() => setEditing(s)} />;
                })}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="panel p-5" data-testid="staff-hours">
        <div className="eyebrow mb-3">Ore settimanali per operatore</div>
        <div className="flex flex-wrap gap-2">
          {staffFor(hid).map((s) => (
            <span key={s.id} className={`badge-x ${(hours[s.id] || 0) > 40 ? "bg-red-50 border-red-200 text-red-700" : "bg-slate-100 border-slate-200 text-slate-600"}`}>
              {s.name} · {hours[s.id] || 0}h {["Jolly", "Back-up"].includes(s.role) && `· ${s.role}`}
            </span>
          ))}
        </div>
      </div>
      {editing && <ShiftDialog key={editing.id} shift={editing} staff={staffFor(hid)} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}
